/**
 * WBProductsApi - Batch product fetching with caching
 * Fetches products by handles or IDs using Storefront GraphQL API
 * Transforms to REST-compatible format for Vue components
 * Depends on WBStorefrontApi being loaded first
 */
window.WBProductsApi = {
  cache: new Map(),

  // Handles the store definitively answered "no such product" for, handle → when it said so.
  // Kept out of `cache` because these expire and cached products do not (see missIsLive).
  misses: new Map(),

  // How long such an answer is trusted. A miss is not a permanent fact about the store: the
  // usual cause is a colour that is merely unpublished right now — a stale Tagalys entry, a
  // release going live during the visit — and pdp-color-switch turns a null product into a
  // full page reload. Without an expiry, one 404 at page load degraded that colour to a
  // reload on every click for the rest of the session, while the HTML fragment for the same
  // handle re-fetched successfully after five minutes with nothing to reconcile the two.
  // Matched to that fragment's TTL (pdp-color-switch.js HTML_CACHE_TTL_MS) so the two halves
  // of a switch stop disagreeing about whether a colour exists.
  MISS_TTL_MS: 5 * 60 * 1000,

  // In-flight lite lookups, handle → Promise, cleared when the lookup settles. The cache
  // alone cannot de-duplicate these: it is only written once a request comes back, so the
  // hover preload and the click that follows it before it lands both saw an empty cache and
  // both issued the same GraphQL document — on the request the colour switch waits on.
  pendingHandles: Object.create(null),

  // Handles per batched GraphQL document (see fetchByHandles). Kept well inside the
  // Storefront API's per-request cost limit: the whole request is rejected once that
  // limit is crossed, and a rejected request yields no products at all.
  HANDLE_BATCH_SIZE: 50,

  // REST fallbacks in flight at once (see fetchHandleChunk). Splitting the GraphQL path into
  // one-document-at-a-time chunks buys nothing if the fallback for a failed chunk then fires
  // a request per handle simultaneously — up to HANDLE_BATCH_SIZE of them, ten times the
  // burst the chunking exists to avoid, on the request the colour switch is waiting on and
  // against a browser that will only open a handful of connections per host anyway.
  REST_FALLBACK_CONCURRENCY: 6,

  // The selection set used for every product in the by-handle lookup (see
  // buildHandleQuery, which wraps one aliased product(handle:) around it per handle).
  //
  // This path exists solely to warm/serve the PDP colour switch (pdp-color-switch.js:
  // preload + switchColor), which reads only id / handle / title / availability / price
  // and the first variant id — everything visual (images, sizes, price display, badges)
  // comes from the ?view=pdp-switch HTML fragment, not from here. So the selection is
  // deliberately minimal: no images, no media, no per-variant details. For a colour family
  // of N colours this is the difference between N × (50 image URLs + 100 variant objects)
  // and N × (one variant id), on the request that warms every swatch on page load.
  // If another consumer ever needs full product data by handle, fetch by id (idQuery /
  // transformProduct stay full-featured) rather than re-fattening this selection.
  //
  // Lookups are exact — one aliased product(handle:) each — not products(query:
  // 'handle:"..."'). That search filter is ignored on this store: products(first: 100,
  // query: 'handle:"anything"') returns the same arbitrary 100 products for every input,
  // including handles that do not exist, so a colour resolved only when it happened to
  // fall inside that window and every other one paid an extra REST round trip on the
  // request the colour switch is waiting on. product(handle:) returns the product or
  // null, and null actually means "no such product".
  handleQueryFields: `
      id
      handle
      title
      availableForSale
      priceRange {
        minVariantPrice { amount }
      }
      variants(first: 1) {
        nodes { id }
      }`,

  // Query for fetching products by IDs
  idQuery: `query getProductsByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        handle
        title
        tags
        productType
        availableForSale
        priceRange {
          minVariantPrice { amount currencyCode }
          maxVariantPrice { amount currencyCode }
        }
        compareAtPriceRange {
          minVariantPrice { amount }
        }
        images(first: 50) {
          nodes { url }
        }
        media(first: 10) {
          nodes {
            mediaContentType
            ... on Video {
              sources { url mimeType }
            }
          }
        }
        variants(first: 100) {
          nodes {
            id
            sku
            title
            price { amount }
            compareAtPrice { amount }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
    }
  }`,

  /**
   * Transform GraphQL product to REST-compatible format
   * Matches structure expected by Vue components
   */
  transformProduct: function(gql) {
    if (!gql) return null;

    var priceMin = Math.round(parseFloat(gql.priceRange.minVariantPrice.amount) * 100);
    var compareAtPrice = gql.compareAtPriceRange.minVariantPrice
      ? Math.round(parseFloat(gql.compareAtPriceRange.minVariantPrice.amount) * 100)
      : null;

    return {
      id: parseInt(WBStorefrontApi.toNumericId(gql.id)),
      handle: gql.handle,
      title: gql.title,
      tags: gql.tags,
      product_type: gql.productType,
      available: gql.availableForSale,
      price: priceMin,
      price_min: priceMin,
      compare_at_price: compareAtPrice,
      price_ratio: compareAtPrice && compareAtPrice > priceMin
        ? Math.round((1 - priceMin / compareAtPrice) * 100)
        : 0,
      variants: gql.variants.nodes.map(function(v) {
        return {
          id: parseInt(WBStorefrontApi.toNumericId(v.id)),
          sku: v.sku,
          title: v.title,
          option1: v.selectedOptions[0] ? v.selectedOptions[0].value : null,
          option2: v.selectedOptions[1] ? v.selectedOptions[1].value : null,
          option3: v.selectedOptions[2] ? v.selectedOptions[2].value : null,
          price: Math.round(parseFloat(v.price.amount) * 100),
          compare_at_price: v.compareAtPrice
            ? Math.round(parseFloat(v.compareAtPrice.amount) * 100)
            : null,
          inventory_quantity: v.availableForSale ? 1 : 0,
          available: v.availableForSale
        };
      }),
      product_image: gql.images.nodes[0] ? gql.images.nodes[0].url : null,
      image: gql.images.nodes[0] ? { src: gql.images.nodes[0].url } : null,
      images: gql.images.nodes.map(function(img) { return { src: img.url }; }),
      media: gql.media.nodes.map(function(m) {
        return {
          media_type: m.mediaContentType.toLowerCase(),
          sources: m.sources || []
        };
      })
    };
  },

  /**
   * Transform the minimal getProductsByHandles node into the small shape the PDP
   * colour switch consumes (id / handle / title / available / price / first variant id).
   * Kept separate from transformProduct so the id path can stay full-featured while the
   * handle path stays lightweight — see handleQueryFields.
   */
  transformProductLite: function(gql) {
    if (!gql) return null;
    var priceMin = Math.round(parseFloat(gql.priceRange.minVariantPrice.amount) * 100);
    return {
      id: parseInt(WBStorefrontApi.toNumericId(gql.id)),
      handle: gql.handle,
      title: gql.title,
      available: gql.availableForSale,
      price: priceMin,
      price_min: priceMin,
      variants: gql.variants.nodes.map(function(v) {
        return { id: parseInt(WBStorefrontApi.toNumericId(v.id)) };
      })
    };
  },

  /**
   * Normalise a REST /products/{handle}.js payload into the same lite shape as
   * transformProductLite, so the fallback path and the GraphQL path are interchangeable.
   * Without this the fallback cached a differently shaped object — REST has no price_min and
   * uses numeric variant ids of its own — and callers would silently read undefined off it.
   */
  transformRestLite: function(rest) {
    if (!rest) return null;
    var priceMin = typeof rest.price_min === 'number' ? rest.price_min : rest.price;
    return {
      id: rest.id,
      handle: rest.handle,
      title: rest.title,
      available: rest.available,
      price: priceMin,
      price_min: priceMin,
      // Truncated to the first variant, matching transformProductLite's variants(first: 1).
      // REST hands back every variant, so without this a lite product's variants length
      // depended on which path happened to serve it — and a consumer reading variants.length
      // (or indexing past [0]) got a different answer for the same handle run to run.
      variants: rest.variants && rest.variants[0] ? [{ id: rest.variants[0].id }] : []
    };
  },

  /**
   * Build the batched exact-lookup document: one aliased product(handle:) field per handle.
   * Aliases are positional (p0, p1, …) so the caller maps a result back by index — handles
   * contain hyphens and cannot be used as GraphQL aliases directly.
   *
   * Handles are passed as query variables ($h0, $h1, …) rather than concatenated into the
   * document. They originate in the DOM (data-handle attributes, Tagalys payloads) and
   * normalizeHandle does no character-class validation, so inlining them made the document
   * only as well-formed as its least trustworthy input. A lone surrogate is enough:
   * JSON.stringify emits it as the escape \uD800, and that is not a valid GraphQL
   * EscapedUnicode, so the batch became a syntax error. (Verified against graphql-js;
   * note U+2028/U+2029 are *not* a problem here — unlike JavaScript, GraphQL does not
   * treat them as line terminators, so they are legal raw inside a string literal.)
   * Because WBStorefrontApi.fetch discards partial data whenever the response carries
   * `errors`, one bad handle cost every handle in the request a per-handle REST fallback.
   * As variables the handles travel as JSON and are never parsed as GraphQL source.
   *
   * @param {Array<string>} handles - Product handles
   * @returns {{query: string, variables: Object}|null} Document and its variables, or null
   *   when there is nothing to ask for
   */
  buildHandleQuery: function(handles) {
    // There is no valid document for zero handles: empty variable-definition parentheses
    // and an empty selection set are both GraphQL parse errors, so `query
    // getProductsByHandles() {}` would 400 rather than resolve nothing. fetchByHandles
    // cannot reach this — the uncached early return and the chunk loop both guarantee a
    // non-empty chunk — but this is a public method, so say so instead of emitting it.
    if (!handles || handles.length === 0) return null;

    var fields = this.handleQueryFields;
    var declarations = handles.map(function(h, i) {
      return '$h' + i + ': String!';
    }).join(', ');
    var selections = handles.map(function(h, i) {
      return '\n  p' + i + ': product(handle: $h' + i + ') {' + fields + '\n  }';
    }).join('');
    var variables = {};
    handles.forEach(function(h, i) { variables['h' + i] = h; });
    return {
      query: 'query getProductsByHandles(' + declarations + ') {' + selections + '\n}',
      variables: variables
    };
  },

  /**
   * Normalize handle-like input to a Shopify product handle
   * Supports raw handles, /products/{handle}, and full product URLs
   *
   * Returns null for anything that is not one of those. That matters more than it looks:
   * callers treat a truthy result as a handle they can address a product with, and
   * pdp-color-switch's onSwatchClick has already called preventDefault and
   * stopImmediatePropagation by the time it uses one — so a returned non-handle is not a
   * degraded lookup, it is a guaranteed 404 with the swatch's own href already discarded.
   * Only stripping a leading '/products/' left every other product-URL shape doing exactly
   * that: '/collections/leggings/products/wb-blue' came back as
   * 'collections/leggings/products/wb-blue' and got fetched, and navigated to, verbatim.
   *
   * @param {string} value
   * @returns {string|null}
   */
  normalizeHandle: function(value) {
    if (typeof value !== 'string') return null;
    var handle = value.trim();
    if (!handle) return null;
    handle = handle.replace(/^https?:\/\/[^/]+/i, '');
    // Before the /products/ match, so a '?variant=' or '#fragment' cannot end up inside
    // the captured segment.
    handle = handle.split('?')[0].split('#')[0];

    // A handle has no slashes, so anything that still does is a path, and the only path
    // that names a product is one with a /products/{handle} segment. Matched anywhere, not
    // just at the front: collection-scoped (/collections/x/products/y) and locale-prefixed
    // (/es/products/y) URLs address the same product as the bare form. Anything else is not
    // a product URL, and reporting it as a handle is worse than reporting nothing.
    if (handle.indexOf('/') !== -1) {
      var match = handle.match(/(?:^|\/)products\/([^/]+)/i);
      if (!match) return null;
      handle = match[1];
    }

    return handle ? handle.toLowerCase() : null;
  },

  /**
   * The storefront root for the locale this page is being served under: '/' on the primary
   * locale, '/es/' where Shopify serves the storefront behind a prefix. Shopify publishes it
   * as Shopify.routes.root via content_for_header, always with both slashes.
   *
   * @returns {string} a path beginning and ending in '/'
   */
  localeRoot: function() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (typeof root !== 'string' || root.charAt(0) !== '/') return '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  },

  /**
   * The canonical product URL for a handle, in the locale of the page asking for it.
   *
   * The single URL builder for both this file and pdp-color-switch.js, which delegates here
   * the same way it delegates normalizeHandle. Two copies is the drift this prevents: they
   * agreed on the happy path and disagreed on the failure, so an unencodable handle became a
   * settled miss on one side and a malformed request on the other.
   *
   * Built off localeRoot rather than a bare '/products/' because normalizeHandle deliberately
   * accepts a locale-prefixed product URL and reduces it to a bare handle — so the prefix has
   * to be put back here or it is silently dropped from every URL this produces. The EU store
   * serves its Spanish storefront under /es (shop-settings.liquid), and {{ product.url }},
   * which fashion-color-variant.liquid writes straight into data-handle, carries that prefix.
   * Dropping it fetched the English render of the switch fragment and painted it onto a Spanish
   * page, pushed an unprefixed history entry and canonical, and sent every navigation fallback
   * out of the locale the shopper was browsing.
   *
   * @param {string} handle
   * @returns {string|null} null when the handle cannot be encoded, and therefore cannot
   *   address a product at all — there is nothing to fetch and nowhere to navigate
   */
  productUrl: function(handle) {
    try {
      return this.localeRoot() + 'products/' + encodeURIComponent(handle);
    } catch (e) {
      // encodeURIComponent throws URIError on a lone surrogate.
      return null;
    }
  },

  /**
   * REST fallback for product handle.
   *
   * Resolves to { product, resolved }, never rejects. `resolved` says whether the store
   * actually answered the question: true means "this is the product" or "there is no such
   * product" (a 404), false means the request never got that far — throttled, 5xx, offline,
   * an edge error page. The caller needs the difference because it records permanent misses;
   * caching a transient failure as "no such product" would degrade that colour to a full page
   * reload for the rest of the session (see fetchByHandles).
   *
   * @param {string} handle
   * @returns {Promise<{product: Object|null, resolved: boolean}>}
   */
  fetchByHandleRest: function(handle) {
    var url = this.productUrl(handle);
    // productUrl returns null for a handle encodeURIComponent cannot encode (a lone
    // surrogate). No such handle can address a product, so report it as a settled miss and
    // let the caller cache it as one — the alternative was a URIError thrown before the
    // .catch below is attached, rejecting the whole batch's promise instead of resolving.
    if (url === null) {
      return Promise.resolve({ product: null, resolved: true });
    }
    return fetch(url + '.js', {
      credentials: 'same-origin'
    })
      .then(function(response) {
        if (response.status === 404) return { product: null, resolved: true };
        if (!response.ok) return { product: null, resolved: false };
        return response.json().then(function(json) {
          return { product: json, resolved: true };
        });
      })
      // Covers a network error and a body that is not JSON. Neither is the store telling us
      // the product does not exist, so neither counts as settled.
      .catch(function() { return { product: null, resolved: false }; });
  },

  /**
   * Cache key for a lite (handle-fetched) product.
   *
   * Lite results are deliberately partial — no images, tags or per-variant detail (see
   * handleQueryFields) — so they get their own key namespace. A null value is a recorded
   * miss: a handle both sources definitively answered "no such product" for. A handle whose
   * lookup merely failed is never written here — see fetchByHandles.
   * Sharing the bare handle key with
   * fetchByIds' full objects meant whichever path ran first decided what a caller got, and a
   * consumer expecting product.images would silently read undefined. fetchByIds still also
   * caches its full result under the bare handle, so that path is unaffected.
   */
  liteKey: function(handle) {
    return 'lite:' + handle;
  },

  /**
   * Look a handle up, preferring a full product (cached by fetchByIds) over a lite one.
   */
  getCachedByHandle: function(handle) {
    if (!handle) return null;
    if (this.cache.has(handle)) return this.cache.get(handle) || null;
    return this.cache.get(this.liteKey(handle)) || null;
  },

  hasCachedByHandle: function(handle) {
    return this.cache.has(handle) || this.cache.has(this.liteKey(handle)) || this.missIsLive(handle);
  },

  /**
   * Whether a recorded miss for this handle is still within MISS_TTL_MS. Expired entries are
   * dropped as they are read, so the map never outgrows the handles actually asked about.
   *
   * A handle that has since been cached as a real product never reaches here — both callers
   * check the cache first — so a leftover entry for one is harmless.
   *
   * @param {string} handle - Normalized handle
   * @returns {boolean}
   */
  missIsLive: function(handle) {
    var at = this.misses.get(handle);
    if (at === undefined) return false;
    if ((Date.now() - at) < this.MISS_TTL_MS) return true;
    this.misses.delete(handle);
    return false;
  },

  /**
   * Fetch products by handles
   * @param {Array<string>} handles - Product handles to fetch
   * @returns {Promise<Array<Object|null>>} Products in same order as input handles
   */
  fetchByHandles: function(handles) {
    var self = this;
    if (!handles || handles.length === 0) {
      return Promise.resolve([]);
    }

    var requestedHandles = handles.map(function(h) { return self.normalizeHandle(h); });
    var validHandles = requestedHandles.filter(function(h) { return Boolean(h); });

    if (validHandles.length === 0) {
      return Promise.resolve(handles.map(function() { return null; }));
    }

    var resolve = function() {
      return requestedHandles.map(function(h) { return self.getCachedByHandle(h); });
    };

    // Check cache first, and de-duplicate: the same colour can be reachable from more than
    // one swatch, and a repeat would otherwise become a second alias for the same product
    // in the same document (fetched and cached twice for one result).
    var seen = Object.create(null);
    var uncached = [];
    validHandles.forEach(function(h) {
      if (seen[h] || self.hasCachedByHandle(h)) return;
      seen[h] = true;
      uncached.push(h);
    });

    if (uncached.length === 0) {
      return Promise.resolve(resolve());
    }

    // Join an in-flight lookup rather than starting a second one for the same handle.
    var joined = [];
    var fresh = [];
    uncached.forEach(function(h) {
      var inFlight = self.pendingHandles[h];
      if (inFlight) joined.push(inFlight);
      else fresh.push(h);
    });

    // Split into fixed-size documents. The Storefront API rejects a request whose total
    // cost exceeds its limit, and WBStorefrontApi.fetch returns null on any `errors` —
    // discarding the products that did resolve. Unbounded batching therefore turned one
    // oversized preload into a per-handle REST fallback for every handle in it.
    var chunks = [];
    for (var i = 0; i < fresh.length; i += self.HANDLE_BATCH_SIZE) {
      chunks.push(fresh.slice(i, i + self.HANDLE_BATCH_SIZE));
    }

    // One chunk at a time, not all at once. Splitting bounds each request's cost; it does not
    // buy the right to fire every part of the split simultaneously. The Storefront API
    // throttles per bucket, so a parallel burst of four documents is likelier to come back
    // throttled than the one oversized document was — and each throttled chunk then costs a
    // REST request per handle. Nothing on the click path is affected: switchColor asks for a
    // single handle, which is a single chunk either way.
    var run = chunks.reduce(function(prev, chunk) {
      return prev.then(function(settled) {
        return self.fetchHandleChunk(chunk)
          // A chunk that throws outright settles nothing: its handles stay uncached and
          // unmarked, so the next call retries them instead of inheriting a recorded miss.
          .catch(function() { return []; })
          .then(function(list) { return settled.concat(list); });
      });
    }, Promise.resolve([]));

    var finished = run.then(function(settled) {
      var definitive = Object.create(null);
      settled.forEach(function(h) { definitive[h] = true; });

      // Record handles the store gave a definitive answer for and that still resolved to
      // nothing. Without this a handle the store has no product for (an unpublished colour
      // still in the Tagalys index, a stale data-handle) stayed permanently uncached, so
      // every hover re-ran the GraphQL query *and* the REST fallback — onSwatchIntent only
      // de-dupes consecutive hovers on one swatch, so a pointer moving between two such
      // swatches re-requested both indefinitely. `misses` marks it, for MISS_TTL_MS;
      // getCachedByHandle finds nothing under either key and reports null, as before.
      //
      // Only definitive answers, though. A handle that merely failed to resolve — throttled,
      // offline, a 5xx — is left uncached so the next attempt retries it. Marking those as
      // misses too meant one network blip pinned that colour to `null` for the rest of the
      // page session, and switchColor turns a null product into a full page reload: the exact
      // navigation the in-place switch exists to avoid, permanently, for a transient fault.
      fresh.forEach(function(h) {
        if (definitive[h] && !self.hasCachedByHandle(h)) self.misses.set(h, Date.now());
      });
    })
    // De-registered however the lookup ends. A registration left behind would make every
    // later call join a promise that has already settled and read a cache nothing populated,
    // wedging those handles for the life of the page.
    .catch(function() { /* nothing resolved, so there is nothing to record */ })
    .then(function() {
      fresh.forEach(function(h) {
        if (self.pendingHandles[h] === finished) delete self.pendingHandles[h];
      });
    });

    // Registered synchronously, before anything is awaited, so a call made later in this same
    // tick (hover then click) sees it and joins instead of re-requesting.
    fresh.forEach(function(h) { self.pendingHandles[h] = finished; });

    return Promise.all(joined.concat([finished])).then(resolve);
  },

  /**
   * Resolve one chunk of handles: batched GraphQL first, REST for whatever it could not
   * resolve. Populates the cache; the caller reads results back out of it.
   *
   * Resolves with the handles the store gave a definitive answer for — found, or confirmed
   * not to exist. Handles whose requests simply failed are deliberately absent, so the caller
   * can tell "no such product" from "ask again later" (see fetchByHandles).
   *
   * @param {Array<string>} chunk - Normalized, de-duplicated, uncached handles
   * @returns {Promise<Array<string>>} Definitively settled handles
   */
  fetchHandleChunk: function(chunk) {
    var self = this;

    // REST_FALLBACK_CONCURRENCY at a time, not the whole list at once — same reasoning as
    // the chunk loop in fetchByHandles, which this would otherwise undo.
    var restFallback = function(list) {
      var settled = [];
      var runBatch = function(batch) {
        return Promise.all(batch.map(function(h) { return self.fetchByHandleRest(h); }))
          .then(function(results) {
            results.forEach(function(result, index) {
              var handle = batch[index];
              var product = self.transformRestLite(result.product);
              if (product) self.cache.set(self.liteKey(handle), product);
              if (result.resolved) settled.push(handle);
            });
          });
      };

      var run = Promise.resolve();
      for (var i = 0; i < list.length; i += self.REST_FALLBACK_CONCURRENCY) {
        (function(batch) {
          run = run.then(function() { return runBatch(batch); });
        })(list.slice(i, i + self.REST_FALLBACK_CONCURRENCY));
      }
      return run.then(function() { return settled; });
    };

    var built = self.buildHandleQuery(chunk);
    // Nothing to ask for: buildHandleQuery has no valid document for an empty chunk, and an
    // empty chunk settles nothing.
    if (!built) return Promise.resolve([]);

    return WBStorefrontApi.fetch(built.query, built.variables)
      .then(function(data) {
        // No data at all means the request itself failed — a transport error, throttling, a
        // document-level `errors` — not that these handles have no products. The whole chunk
        // drops to REST, and only REST decides what counts as definitively absent.
        if (!data) {
          return restFallback(chunk);
        }

        // Cache fetched products by handle. Aliases are positional, so chunk[i] is the
        // handle that was asked for; a null alias means the Storefront API has no such
        // product and that handle drops through to the REST fallback below.
        chunk.forEach(function(handle, i) {
          var node = data['p' + i];
          if (node) {
            var product = self.transformProductLite(node);
            // Keyed by the handle that was *asked for* first, which is the key the REST path
            // uses and the only one `found` below and getCachedByHandle look under. Keying
            // solely on the returned handle meant a response that canonicalised it left the
            // requested handle looking uncached: it fell into `missing`, paid a full REST
            // round trip, cached the same product a second time, and — if REST failed — was
            // never recorded as definitively settled.
            self.cache.set(self.liteKey(handle), product);
            // Also under the returned handle when it differs, so a later lookup by the
            // canonical form resolves from cache too.
            var normalizedNodeHandle = self.normalizeHandle(node.handle);
            if (normalizedNodeHandle && normalizedNodeHandle !== handle) {
              self.cache.set(self.liteKey(normalizedNodeHandle), product);
            }
          }
        });

        var found = chunk.filter(function(h) { return self.hasCachedByHandle(h); });
        var missing = chunk.filter(function(h) { return !self.hasCachedByHandle(h); });
        if (missing.length === 0) {
          return found;
        }

        return restFallback(missing).then(function(settled) {
          return found.concat(settled);
        });
      });
  },

  /**
   * Fetch products by IDs
   * @param {Array<number|string>} ids - Product IDs to fetch
   * @returns {Promise<Array<Object|null>>} Products in same order as input IDs
   */
  fetchByIds: function(ids) {
    var self = this;
    if (!ids || ids.length === 0) {
      return Promise.resolve([]);
    }

    // Check cache first (by ID)
    var uncached = ids.filter(function(id) { return !self.cache.has('id:' + id); });

    if (uncached.length === 0) {
      return Promise.resolve(ids.map(function(id) { return self.cache.get('id:' + id); }));
    }

    // Fetch uncached products
    var gids = WBStorefrontApi.toGids('Product', uncached);
    return WBStorefrontApi.fetch(this.idQuery, { ids: gids })
      .then(function(data) {
        if (!data || !data.nodes) {
          return ids.map(function(id) { return self.cache.get('id:' + id) || null; });
        }

        // Cache fetched products by ID and handle
        data.nodes.forEach(function(node) {
          if (node) {
            var product = self.transformProduct(node);
            var numericId = WBStorefrontApi.toNumericId(node.id);
            self.cache.set('id:' + numericId, product);
            var normalizedNodeHandle = self.normalizeHandle(node.handle);
            if (normalizedNodeHandle) {
              self.cache.set(normalizedNodeHandle, product);
            }
          }
        });

        // Return in original order
        return ids.map(function(id) { return self.cache.get('id:' + id) || null; });
      });
  }
};
